import React, { useState, useEffect } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useUserContext } from '../context/users/UserContext'
import { login, sendRestoreUrl } from '../context/users/UserActions'
import '../styles/Login.css'
import { FaEnvelope, FaLock, FaQuestionCircle, FaShoppingCart, FaSpinner, FaUserPlus, FaUser, FaStore, FaSignInAlt } from 'react-icons/fa'
import { Form } from 'react-bootstrap'
import { Link } from 'react-router-dom'

const EMAIL_REGEX = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,3}$/

export default function Login() {
  const navigate = useNavigate()
  const { authenticateUser, isAuthenticated, setToken, setAlert } = useUserContext()

  const [username, setUsername] = useState('')
  const [password, setPass] = useState('')
  const [validEmail, setValidEmail] = useState(true)
  const [validPassword, setValidPassword] = useState(true)
  const [touched, setTouched] = useState({ email: false, password: false })
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleChangeEmail = (e) => {
    const value = e.target.value
    setUsername(value)
    if(value === '') {
      setValidEmail(true)
    } else {
      setValidEmail(EMAIL_REGEX.test(value))
    }
    setErrorMessage('')
  }

  const handleChangePass = (e) => {
    const value = e.target.value
    setPass(value)
    setValidPassword(value.length >= 6)
    setErrorMessage('')
  }

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  const getInputClasses = (isValid, isTouched) => {
    return `form-control ${!isValid && isTouched ? 'is-invalid' : ''} ${isValid && isTouched ? 'is-valid' : ''}`
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setErrorMessage('')

    if(!validEmail) {
      setErrorMessage('Por favor ingresa un correo electrónico válido')
      setIsLoading(false)
    } else if(username === '' || password === '') {
      setErrorMessage('Por favor ingresa todos los datos requeridos')
      setIsLoading(false)
    } else {
      try {
        let userData = await login({ 
          path: 'users/login',
          data: {
            userEmail: username.trim(),
            userPass: password,
          }, 
          token: '' 
        })

        if(Object.prototype.hasOwnProperty.call(userData, 'token')) {
          userData = {
            ...userData,
            value: userData.token
          }
          authenticateUser(userData)
          setToken(userData.token)
          navigate('/home')
        } else {
          setErrorMessage(userData.message || 'Error al iniciar sesión')
        }
      } catch (error) {
        setErrorMessage('Error en el servidor. Por favor intentá nuevamente más tarde.')
      }
      setIsLoading(false)
    }
  }

  const handleRecoverPass = async (e) => {
    e.preventDefault()
    if(username === '') {
      setErrorMessage('Para recuperar la contraseña, por favor ingresá tu correo')
    } else {
      setIsLoading(true)
      try {
        const userData = await sendRestoreUrl(username.trim())
        if(Object.prototype.hasOwnProperty.call(userData, 'error')) {
          setErrorMessage('Error en el servicio de notificaciones. Por favor contactá a soporte.')
        } else if (userData.includes('Hemos enviado tu nueva contraseña al correo:')) {
          const email = userData.replace('Hemos enviado tu nueva contraseña al correo: ', '')
          setAlert({
            message: `Hemos enviado tu nueva contraseña al correo ${email}. Revisá tu bandeja de entrada.`,
            msgType: 'success'
          })
          setUsername('')
          setPass('')
        } else {
          setErrorMessage(userData)
        }
      } catch (error) {
        setErrorMessage('Error en el servidor. Por favor intentá nuevamente más tarde.')
      }
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setAlert({
      message: '¡Bienvenido a nuestra plataforma! Estamos en constante mejora y valoramos tu feedback.',
      msgType: 'info'
    })
  }, [])

  if (isAuthenticated) {
    return <Navigate replace to={'/home'} />
  }

  return (
    <div className="login-container">
      <div className="login-layout">
        {/* Sección izquierda - Imagen */}
        <div className="login-image-section">
          <div className="login-image-container">
            <img 
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" 
              alt="Bienvenido a nuestra plataforma" 
              className="login-hero-image" 
            />
            <div className="login-image-overlay">
              <div className="overlay-content">
                <div className="logo-container-overlay">
                  <img
                    src="/logo-develemos.png"
                    alt="Develemos"
                    className="develemos-logo-img"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sección derecha - Formulario de login */}
        <div className="login-form-section">
          <div className="login-card">
            <h1 className="login-title">¡Bienvenido!</h1>
            <p className="login-subtitle">Si aún no te registraste, presioná el botón verde.</p>

            {/* Botones principales para nuevos usuarios */}
            <div className="primary-actions">
              <div className="primary-action-buttons">
                <Link to="/hostUser" className="primary-action-link buy-link">
                  <FaShoppingCart className="login-action-icon" />
                  <div className="action-content">
                    <span className="action-title">Quiero registrarme</span>
                    <span className="action-subtitle">Creá una cuenta y probalo gratis</span>
                  </div>
                </Link>
              </div>
            </div>

            <p className="login-subtitle">
              Si ya tenés cuenta, ingresá tu correo y contraseña y presioná el botón naranja.
            </p>

            {/* Formulario de login compacto para usuarios existentes */}
            <div className="existing-user-login">
              {errorMessage && (
                <div className="error-message">
                  {errorMessage}
                </div>
              )}

              <Form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  <FaEnvelope className="label-icon" />
                  Correo electrónico
                </label>
                <div className="input-container">
                  <input
                    type="email"
                    className={getInputClasses(validEmail, touched.email)}
                    id="email"
                    name="email"
                    value={username}
                    onChange={handleChangeEmail}
                    onBlur={() => handleBlur('email')}
                    disabled={isLoading}
                    autoComplete="email"
                    aria-describedby="emailHelp emailError"
                    placeholder="ejemplo@correo.com"
                    required
                  />
                </div>
                {!validEmail && touched.email && (
                  <div id="emailError" className="invalid-feedback show">
                    Por favor ingresá un correo electrónico válido
                  </div>
                )}
                <small id="emailHelp" className="form-text">
                  Nunca compartiremos tu correo con terceros
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  <FaLock className="label-icon" />
                  Contraseña
                </label>
                <div className="input-container">
                  <input
                    type="password"
                    className={getInputClasses(validPassword, touched.password)}
                    id="password"
                    name="password"
                    value={password}
                    onChange={handleChangePass}
                    onBlur={() => handleBlur('password')}
                    disabled={isLoading}
                    autoComplete="current-password"
                    aria-describedby="passwordHelp passwordError"
                    placeholder="Ingresá tu contraseña"
                    required
                  />
                </div>
                {!validPassword && touched.password && (
                  <div id="passwordError" className="invalid-feedback show">
                    La contraseña debe tener al menos 6 caracteres
                  </div>
                )}
              </div>

              <div className="forgot-password-container">
                <button 
                  type="button"
                  onClick={handleRecoverPass}
                  className="forgot-password-link"
                  disabled={isLoading}
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              <button type="submit" className="login-button" disabled={isLoading}>
                {isLoading ? (<FaSpinner className="loading-spinner"/>) :
                 (
                  <>
                  <FaSignInAlt className="login-action-icon"/>
                  <div className="action-content">
                    <span className="action-title">Ingresá</span>
                    <span className="action-subtitle">Solo si ya te registraste</span>
                  </div>
                  </>
                  )}
              </button>
              </Form>
            </div>

            <p className="terms-text">
              Al ingresar a esta plataforma aceptas los{' '}
              <a 
                href="https://develemos.com/politica-privacidad/" 
                target="_blank"
                rel="noopener noreferrer"
              >
                términos y condiciones
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
